import React, { useEffect, useRef, useState } from "react";

const DashboardGrid = ({
  children,
  gap = 24,
  className = "",
  minColumnWidth = 400,
  maxColumnWidth = 719,
  maxColumns = 3,
}) => {
  const containerRef = useRef(null);
  const [columns, setColumns] = useState(1);
  const [columnHeights, setColumnHeights] = useState([]);
  const [itemPositions, setItemPositions] = useState([]);

  // Function to calculate the optimal number of columns
  const calculateColumns = (containerWidth) => {
    // Calculate the maximum number of columns that can fit respecting the minimum width
    const maxPossibleColumns = Math.min(maxColumns, Math.floor(containerWidth / (minColumnWidth + gap)));

    // The actual number of columns is the maximum possible that fits
    return Math.max(1, maxPossibleColumns);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const updateLayout = () => {
      const container = containerRef.current;
      const containerWidth = container.offsetWidth;

      // Calculate the optimal number of columns
      const newColumns = calculateColumns(containerWidth);
      setColumns(newColumns);

      const columnWidth = Math.min(maxColumnWidth, (containerWidth - gap * (newColumns - 1)) / newColumns);

      const heights = new Array(newColumns).fill(0);
      const positions = [];

      const childElements = Array.from(container.children);

      childElements.forEach((child, index) => {
        if (index === 0) return; // Skip the first child which is our sizing helper

        const actualChild = child;

        // Find the column with minimum height
        const minHeightIndex = heights.indexOf(Math.min(...heights));

        // Calculate position
        const left = minHeightIndex * (columnWidth + gap);
        const top = heights[minHeightIndex];

        positions.push({ left, top });

        // Update column height
        heights[minHeightIndex] += actualChild.offsetHeight + gap;
      });

      setColumnHeights([...heights]);
      setItemPositions(positions);
    };

    // Initial layout
    const timeoutId = setTimeout(updateLayout, 0);

    // Listen for resize events
    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(containerRef.current);

    // Listen for child resize events
    const childElements = Array.from(containerRef.current.children);
    childElements.forEach((child) => {
      if (child instanceof HTMLElement) {
        resizeObserver.observe(child);
      }
    });

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [children, gap, minColumnWidth, maxColumnWidth, maxColumns]);

  const containerHeight = Math.max(...(columnHeights.length > 0 ? columnHeights : [0])) - gap;

  return (
    <div
      ref={containerRef}
      className={`dashboardGrid ${className}`}
      style={{
        position: "relative",
        height: containerHeight > 0 ? `${containerHeight}px` : "auto",
        width: "100%",
      }}
    >
      {/* Helper div for measuring container width */}
      <div style={{ position: "absolute", width: "100%", height: 0, visibility: "hidden" }} />

      {children.map((child, index) => (
        <div
          key={index}
          className="gridItem"
          style={{
            position: "absolute",
            left: itemPositions[index]?.left || 0,
            top: itemPositions[index]?.top || 0,
            width: `min(${maxColumnWidth}px, calc((100% - ${gap * (columns - 1)}px) / ${columns}))`,
            transition: "all 0.3s ease",
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

export default DashboardGrid;